import { LegacyAccessService } from '@/core/services/authentication/legacy-access.service';
import { PermissionsService } from '@/core/services/permissions/permissions.service';
import { MeProfile } from '@/core/services/profile/me-profile.model';
import { HomeState } from '@/modules/home/core/state/home.state';
import { NAVBAR_TRANSLATIONS_KEYS } from './app.menu-translations.model';

import { Component, effect, inject, untracked } from '@angular/core';
import { RouterModule } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { MenuItem } from 'primeng/api';
import { LayoutService } from '../service/layout.service';
import { MenuStateService } from '../service/menu-state.service';
import { AppMenuitem } from './app.menuitem';

const LEGACY_CRM_PATH = '/crm/support';

@Component({
  selector: '[app-menu]',
  standalone: true,
  imports: [AppMenuitem, RouterModule],
  template: `
    <ul class="layout-menu">
      @for (item of items; track i; let i = $index) {
        @if (!item.separator) {
          <li app-menuitem [item]="item" [index]="i" [root]="true"></li>
        }
        @if (item.separator) {
          <li class="menu-separator"></li>
        }
      }
    </ul>
  `,
})
export class AppMenu {
  private readonly store = inject(Store);
  private readonly permissions = inject(PermissionsService);
  private readonly layoutService = inject(LayoutService);
  private readonly menuStateService = inject(MenuStateService);
  private translateService = inject(TranslateService);
  private readonly legacyAccess = inject(LegacyAccessService);

  me = this.store.selectSignal<MeProfile>(HomeState.getMe);
  constructor() {
    effect(() => {
      const meUser = this.me();
      const legacyStatus = this.legacyAccess.startUrl().status;
      if (meUser) {
        if (legacyStatus === 'idle') {
          untracked(() => this.legacyAccess.loadStartUrl());
        }
        this.initializeMenuItems();
      }
    });
  }

  items: MenuItem[] = [];
  topbarItems: MenuItem[] = [];
  sidebarItems: MenuItem[] = [];

  initializeMenuItems() {
    const meUser = this.me();

    if (!meUser) return;

    this.translateService.stream(NAVBAR_TRANSLATIONS_KEYS).subscribe({
      next: (translations) => {
        const isPrivileged = this.permissions.isAdmin();
        const canSeeCameraStatusItem =
          isPrivileged || !!meUser.cameraGroupPermissions?.canSeeCameraStatus;
        const canSeeLprBlacklist = !!meUser?.permissions?.some(
          (p) => p.permission_key === 'can_see_lpr_blacklist' && !!p.enabled,
        );
        const canSeeLprMenu =
          this.permissions.hasPermission('can_see_lpr_menu');
        const canSeeCameraMenu = this.permissions.hasPermission(
          'can_see_camera_menu',
        );
        const canSeeCameraAutomationMenu = this.permissions.hasPermission(
          'can_see_camera_automation_menu',
        );
        const canSeeFacialMenu = this.permissions.hasPermission(
          'can_see_facial_menu',
        );
        const canSeePeopleCountingMenu = this.permissions.hasPermission(
          'can_see_people_counting_menu',
        );
        const canSeeVehicleCountingMenu = this.permissions.hasPermission(
          'can_see_vehicle_counting_menu',
        );
        const canSeeSecurityForcesMenu = this.permissions.hasPermission(
          'can_see_security_forces_menu',
        );
        const canSeeMoniIntegrationMenu = this.permissions.hasPermission(
          'can_see_moni_integration_menu',
        );

        const cameraMenuItems: MenuItem[] = canSeeCameraMenu
          ? [
              {
                label: translations.minhas_cameras,
                icon: 'pi pi-video',
                routerLink: '/minhas-cameras',
                routerLinkActiveOptions: { exact: true },
              },
              {
                label: translations.meus_videos,
                icon: 'pi pi-server',
                routerLink: 'meus-videos',
                routerLinkActiveOptions: { exact: true },
              },
              {
                label: translations.camera_registration,
                icon: 'pi pi-camera',
                routerLink: 'cadastro-camera',
                routerLinkActiveOptions: { exact: true },
              },
              {
                label: translations.mosaic.mosaic,
                icon: 'pi pi-th-large',
                routerLink: 'mosaic',
              },
              {
                label: translations.patrol.patrol,
                icon: 'pi pi-directions',
                routerLink: 'patrol',
              },
              ...(canSeeCameraStatusItem
                ? [
                    {
                      label: translations.cameras_dashboard,
                      icon: 'pi pi-chart-bar',
                      routerLink: 'minhas-cameras/cameras-dashboard',
                      routerLinkActiveOptions: { exact: true },
                    },
                  ]
                : []),
            ]
          : [];

        const cameraAutomationItems: MenuItem[] =
          canSeeCameraAutomationMenu && canSeeCameraMenu
            ? [
                {
                  label: translations.automation_menu,
                  icon: 'pi pi-cog',
                  items: [
                    {
                      label: translations.devices_menu,
                      icon: 'pi pi-microchip',
                      routerLink: 'automacao/dispositivos',
                      routerLinkActiveOptions: { exact: true },
                    },
                    {
                      label: translations.rules_engine_menu,
                      icon: 'pi pi-sitemap',
                      routerLink: 'automacao/motor-de-regras',
                      routerLinkActiveOptions: { exact: true },
                    },
                  ],
                },
              ]
            : [];

        const camerasItems: MenuItem[] = [
          ...cameraMenuItems,
          ...cameraAutomationItems,
        ];

        const lprItems: MenuItem[] = canSeeLprMenu
          ? [
              {
                label: translations.lpr_legacy_menu_title,
                icon: 'pi pi-placa-menu',
                routerLink: 'lpr',
                routerLinkActiveOptions: { exact: true },
              },
              ...(canSeeLprBlacklist
                ? [
                    {
                      label: translations.lpr_blacklist_groups_menu_title,
                      icon: 'pi pi-ban',
                      routerLink: 'lpr-blacklist-groups',
                      routerLinkActiveOptions: { exact: true },
                    },
                  ]
                : []),
              {
                label: translations.lpr_dashboard_menu_title,
                icon: 'pi pi-chart-line',
                routerLink: 'lpr-dashboard',
                routerLinkActiveOptions: { exact: true },
              },
            ]
          : [];

        const sidebarItems: MenuItem[] = [
          ...(camerasItems.length
            ? [
                {
                  label: translations.cameras,
                  icon: 'pi pi-video',
                  items: camerasItems,
                },
              ]
            : []),
          ...(lprItems.length
            ? [
                {
                  label: translations.lpr_menu,
                  icon: 'pi pi-placa-menu',
                  items: lprItems,
                },
              ]
            : []),
          this.crmMenuItem(
            translations.crm_menu,
            translations.crm_menu_unavailable,
            meUser.whitelabel?.mainDomain,
          ),
        ];

        if (
          meUser.whitelabel?.services?.has_face_recognition &&
          canSeeFacialMenu
        ) {
          const canSeeFaceComparison =
            !!meUser.cameraGroupPermissions?.canSeeFaceComparisonEvents;

          sidebarItems.push({
            label: translations.facial,
            icon: 'pi pi-face-smile',
            items: [
              {
                label: translations.management,
                icon: 'pi pi-cog',
                routerLink: 'facial-registration',
                routerLinkActiveOptions: { exact: true },
              },
              ...(canSeeFaceComparison
                ? [
                    {
                      label: translations.face_not_identified_title,
                      icon: 'pi pi-user-minus',
                      routerLink: 'face-not-identified',
                      routerLinkActiveOptions: { exact: true },
                    },
                    {
                      label: translations.compare,
                      icon: 'pi pi-th-large',
                      routerLink: 'compare-face',
                      routerLinkActiveOptions: { exact: true },
                    },
                  ]
                : []),
              ...(meUser.whitelabel?.services?.has_facial_embedded
                ? [
                    {
                      label: translations.group_management,
                      icon: 'pi pi-address-book',
                      routerLink: 'group-management',
                      routerLinkActiveOptions: { exact: true },
                    },
                  ]
                : []),
              {
                label: translations.group_person,
                icon: 'pi pi-users',
                routerLink: 'group-person',
                routerLinkActiveOptions: { exact: true },
              },
              {
                label: translations.facial_notification_rules_menu,
                icon: 'pi pi-bell',
                routerLink: 'facial-notification-rules',
                routerLinkActiveOptions: { exact: true },
              },
            ],
          });
        }

        const canSeePeopleCountingItem =
          !!meUser.whitelabel?.services?.has_people_counting &&
          !!meUser.cameraGroupPermissions?.canSeePeopleCounting &&
          canSeePeopleCountingMenu;

        const canSeeVehicleCountingItem =
          !!meUser.whitelabel?.services?.has_vehicle_counting &&
          !!meUser.cameraGroupPermissions?.canSeeVehicleCounting &&
          canSeeVehicleCountingMenu;

        const analyticsItems: MenuItem[] = [];
        if (canSeePeopleCountingItem) {
          analyticsItems.push({
            label: translations.people_counting,
            icon: 'pi pi-stopwatch',
            routerLink: 'analitycs/people-counting',
            routerLinkActiveOptions: { exact: true },
          });
        }
        if (canSeeVehicleCountingItem) {
          analyticsItems.push({
            label: translations.vehicle_counting,
            icon: 'pi pi-stopwatch',
            routerLink: 'analitycs/vehicle-counting',
            routerLinkActiveOptions: { exact: true },
          });
        }

        if (analyticsItems.length) {
          sidebarItems.push({
            label: translations.analytics,
            icon: 'pi pi-chart-bar',
            items: analyticsItems,
          });
        }

        const integrationItems: MenuItem[] = [];
        if (
          meUser.whitelabel?.services?.has_platform_integration &&
          canSeeSecurityForcesMenu
        ) {
          integrationItems.push({
            label: translations.security_forces_projects,
            icon: 'pi pi-building-columns',
            routerLink: '/integrations-gov',
            routerLinkActiveOptions: { exact: true },
          });
        }

        if (
          meUser.whitelabel?.services?.has_platform_integration &&
          (canSeeMoniIntegrationMenu || meUser.admin || meUser.masterAdmin)
        ) {
          integrationItems.push({
            label: translations.moni_integration,
            icon: 'pi pi-shield',
            routerLink: '/integrations-moni',
            routerLinkActiveOptions: { exact: true },
          });
        }

        if (integrationItems.length) {
          sidebarItems.push({
            label: translations.integrations_menu,
            icon: 'pi pi-briefcase',
            items: integrationItems,
          });
        }

        const userPermissions =
          meUser?.permissions?.map((p) => p.permission_key) || [];

        const canSeeHorus =
          userPermissions.includes('can_see_horus_tab') &&
          !!meUser.whitelabel?.services?.is_horus_enabled;

        if (canSeeHorus) {
          sidebarItems.push({
            label: translations.horus,
            icon: 'pi pi-microchip-ai',
            routerLink: 'horus',
            routerLinkActiveOptions: { exact: true },
          });
        }

        const topbarItems: MenuItem[] = [
          {
            label: translations.home,
            icon: 'pi pi-home',
            routerLink: '/minhas-cameras',
            routerLinkActiveOptions: { exact: true },
          },
          this.crmMenuItem(
            translations.crm_menu,
            translations.crm_menu_unavailable,
            meUser.whitelabel?.mainDomain,
          ),
          {
            label: translations.horus,
            icon: 'pi pi-microchip-ai',
            routerLink: canSeeHorus ? 'horus' : undefined,
            disabled: !canSeeHorus,
            class: canSeeHorus
              ? 'horus-menu-item'
              : 'horus-menu-item horus-menu-item-disabled',
            tooltip: canSeeHorus
              ? ''
              : 'Seu perfil não possui acesso ao módulo Horus',
            routerLinkActiveOptions: { exact: true },
          },
        ];

        this.topbarItems = topbarItems;
        const cameraSidebarItem = sidebarItems.find(
          (item) => item.label === translations.cameras,
        );

        this.sidebarItems = [
          ...(cameraSidebarItem ? [cameraSidebarItem] : []),
          ...sidebarItems.filter(
            (item) =>
              item.label !== translations.cameras &&
              item.label !== translations.crm_menu &&
              item.label !== translations.horus,
          ),
        ];

        this.items = this.topbarItems;

        this.menuStateService.setTopbarItems(this.topbarItems);
        this.menuStateService.setSidebarItems(this.sidebarItems);
      },
      error: (err) => console.error('Translation error:', err),
    });
  }

  private crmMenuItem(
    label: string,
    unavailableTooltip: string,
    mainDomain?: string,
  ): MenuItem {
    const item: MenuItem = { label, icon: 'pi pi-database' };
    const disabledClass = 'opacity-50 !cursor-not-allowed';
    const startUrl = this.legacyAccess.startUrl();

    switch (startUrl.status) {
      case 'ready':
        return {
          ...item,
          command: () =>
            window.open(
              this.legacyAccess.legacyPageUrl(startUrl.url, LEGACY_CRM_PATH),
              '_blank',
            ),
        };
      case 'failed':
        return {
          ...item,
          command: () =>
            window.open(`${mainDomain}${LEGACY_CRM_PATH}`, '_blank'),
        };
      case 'unavailable':
        return {
          ...item,
          disabled: true,
          class: disabledClass,
          tooltip: unavailableTooltip,
        };
      default:
        return { ...item, disabled: true, class: disabledClass };
    }
  }
}
